import { useMemo, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { DataGrid, GridColDef, GridToolbar, GridRowSelectionModel } from '@mui/x-data-grid';
import { Menu, MenuItem } from '@mui/material';
import { MoreVertical } from 'react-feather';
import type { MarketingMessage, MessageGenPhase } from '@trinity/chat';

export interface MessageGenerationTableProps {
  messages: MarketingMessage[];
  isGenerating: boolean;
  sessionId?: number;
  numberOfMessages?: number;
  onSaveSelected?: (messageIdentifiers: string[]) => void;
  onResetSession?: () => void;
  isSaving?: boolean;
  phase?: MessageGenPhase;
}

/**
 * Check if messages can be saved based on the current phase.
 * Messages can only be saved after curation is complete.
 */
function canSaveMessages(phase?: MessageGenPhase): boolean {
  return phase === 'curated';
}

export function MessageGenerationTable({
  messages,
  isGenerating,
  sessionId,
  numberOfMessages = 5,
  onSaveSelected,
  onResetSession,
  isSaving = false,
  phase,
}: MessageGenerationTableProps): React.JSX.Element {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);

  const savingEnabled = canSaveMessages(phase);

  // Auto-select saved messages when messages load or change
  useEffect(() => {
    const savedIds = messages.filter((m) => m.saved).map((m) => m.identifier);
    if (savedIds.length > 0) {
      setSelectionModel((prev) => {
        // Merge saved IDs with current selection, avoiding duplicates
        const unsavedInSelection = prev.filter((id) => !savedIds.includes(id as string));
        return [...savedIds, ...unsavedInSelection];
      });
    }
  }, [messages]);
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        width: 200,
        flex: 1,
        renderCell: (params) => <TitleCell>{params.value}</TitleCell>,
      },
      {
        field: 'body',
        headerName: 'Message',
        width: 350,
        flex: 2,
        renderCell: (params) => <MessageCell>{params.value}</MessageCell>,
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 150,
        renderCell: (params) => <CategoryBadge>{params.value}</CategoryBadge>,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => (
          <StatusBadge status={params.value as 'draft' | 'approved' | 'published'}>
            {params.value}
          </StatusBadge>
        ),
      },
    ],
    []
  );

  // Create rows with id field required by DataGrid
  const rows = useMemo(
    () =>
      messages.map((msg) => ({
        ...msg,
        id: msg.identifier || index, // Use message id or fallback to index
      })),
    [messages]
  );

  // Generate skeleton rows while loading
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: Math.min(numberOfMessages, 10) }).map((_, index) => ({
        id: `skeleton-${index}`,
        title: '',
        body: '',
        category: '',
        status: 'draft' as const,
      })),
    [numberOfMessages]
  );

  return (
    <Root>
      <PanelHeader>
        <HeaderLeft>
          <HeaderTitle>Generated Messages</HeaderTitle>
          <MessageCount>{messages.length} messages</MessageCount>
        </HeaderLeft>
        {(onSaveSelected || onResetSession) && (
          <>
            <ActionsButton
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
              disabled={isSaving}
            >
              Actions
            </ActionsButton>
            <Menu
              anchorEl={actionsAnchorEl}
              open={Boolean(actionsAnchorEl)}
              onClose={() => setActionsAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  // Only save unsaved messages
                  const unsavedSelected = (selectionModel as string[]).filter((id) => {
                    const message = messages.find((m) => m.identifier === id);
                    return message && !message.saved;
                  });
                  if (unsavedSelected.length > 0) {
                    onSaveSelected(unsavedSelected);
                  }
                  setActionsAnchorEl(null);
                }}
                disabled={(() => {
                  // Disable if saving not enabled (not at curated phase)
                  if (!savingEnabled) return true;
                  // Disable if no unsaved messages are selected
                  const unsavedCount = (selectionModel as string[]).filter((id) => {
                    const message = messages.find((m) => m.identifier === id);
                    return message && !message.saved;
                  }).length;
                  return unsavedCount === 0 || !sessionId;
                })()}
              >
                Save Selected Messages ({(() => {
                  const unsavedCount = (selectionModel as string[]).filter((id) => {
                    const message = messages.find((m) => m.identifier === id);
                    return message && !message.saved;
                  }).length;
                  return unsavedCount;
                })()})
              </MenuItem>
              <MenuItem
                onClick={() => {
                  window.open('/message-library', '_blank');
                  setActionsAnchorEl(null);
                }}
              >
                View Message Library
              </MenuItem>
              {onResetSession && (
                <MenuItem
                  onClick={() => {
                    onResetSession();
                    setActionsAnchorEl(null);
                  }}
                >
                  Reset Session
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </PanelHeader>

      <DataGridContainer>
        <DataGrid
          rows={isGenerating ? skeletonRows : rows}
          columns={columns}
          loading={isGenerating}
          checkboxSelection={savingEnabled}
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) => {
            if (!savingEnabled) return;
            // Keep saved messages in selection (they remain checked and disabled)
            const savedIds = messages.filter((m) => m.saved).map((m) => m.identifier);
            const unsavedSelection = newSelection.filter((id) => {
              const message = messages.find((m) => m.identifier === id);
              return !message?.saved;
            });
            setSelectionModel([...savedIds, ...unsavedSelection]);
          }}
          isRowSelectable={(params) => savingEnabled && !params.row.saved}
          getRowClassName={(params) => (params.row.saved ? 'saved-row' : '')}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: { fileName: `messages-session-${sessionId}` },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0',
              fontSize: '11px',
              fontWeight: 600,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            },
            '& .MuiDataGrid-row': {
              '&.saved-row': {
                backgroundColor: 'rgba(16, 185, 129, 0.1) !important',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(18, 113, 196, 0.08) !important',
              },
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9f9f9',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              color: '#424242',
              borderBottom: '1px solid #e8e8e8',
            },
            '& .MuiCheckbox-root.Mui-disabled': {
              color: '#d1d5db',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px 16px',
              borderBottom: '1px solid #e0e0e0',
              gap: '8px',
            },
            '& .MuiDataGrid-overlay': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
          }}
        />
      </DataGridContainer>
    </Root>
  );
}

// Styled Components
const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const HeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionsButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.disabled ? '#a0a0a0' : props.theme.palette.sentiment.primary.main)};
  border: none;
  border-radius: 6px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(props) => props.theme.palette.sentiment.primary.dark};
  }
`;

const MessageCount = styled.span`
  font-size: 13px;
  color: #666;
  padding: 4px 10px;
  background: #f5f5f5;
  border-radius: 10px;
`;

const DataGridContainer = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  width: 100%;
`;

const TitleCell = styled.div`
  font-weight: 500;
  color: #1a1a1a;
`;

const MessageCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
`;

const CategoryBadge = styled.span`
  padding: 4px 10px;
  background: ${(props) => props.theme.palette.sentiment.primary.lighter};
  color: ${(props) => props.theme.palette.sentiment.primary.main};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: 'draft' | 'approved' | 'published' }>`
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#fef3c7';
      case 'approved':
        return '#d1fae5';
      case 'published':
        return '#dbeafe';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#92400e';
      case 'approved':
        return '#065f46';
      case 'published':
        return '#1e40af';
      default:
        return '#666';
    }
  }};
`;
